import { Component, inject, OnInit, signal, ViewChild } from '@angular/core';
import { CustomFilter, DynamicColumn, TableComponent } from '../../../@shared/components/table-component/table-component';
import { Params } from '../../../services/utilis/add-params-service';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { distinctUntilChanged, map, switchMap, tap } from 'rxjs';
import { PaginatorState } from 'primeng/paginator';
import { AddEmployeeForm } from '../../../@shared/forms/employees/add-employee-form/add-employee-form';
import { UpdateEmployeeForm } from '../../../@shared/forms/employees/update-employee-form/update-employee-form';
import { DeleteEmployeeForm } from '../../../@shared/forms/employees/delete-employee-form/delete-employee-form';
import { EmployeeService } from '../../../services/employees/employee-service';
import { Employee } from '../../../models/employee';
import { enumToOptions } from '../../../@shared/utility/enumToOptions';
import { UserRole, UserStatus } from '../../../models/enums';
import { EmployeeActivityLogList } from '../../setting/employee-activity-log/employee-activity-log-list/employee-activity-log-list';

@Component({
  selector: 'app-employees-list',
  imports: [TableComponent, AddEmployeeForm, UpdateEmployeeForm, DeleteEmployeeForm, EmployeeActivityLogList],
  templateUrl: './employees-list.html',
  styleUrl: './employees-list.scss'
})
export class EmployeesList implements OnInit {

  params = [
    "page",
    "size",
    "search",
    'sortBy',
    'sortDirection',
    'role',
    'status',
    'createdFrom',
    'createdTo',
  ]

  modifiedParams: Params = {
    page: "page",
    size: "size",
    search: "search",
    sortBy: "sortBy",
    sortDirection: "sortDirection",
    role: "role",
    status: "status",
    createdFrom: "createdFrom",
    createdTo: "createdTo",
  }

  // Define columns to show in the table
  tableColumns: DynamicColumn<Employee>[] = [
    { header: 'ID', field: 'id', sortable: true, },
    { header: 'Name', field: 'fullName', bottomField: 'email', isAvatar: true, sortable: true },
    {
      header: 'Role',
      field: 'role',
      sortable: true,
      isCapitalize: true,
    },
    {
      header: 'Date created',
      field: 'createdAt',
      isDateTime: true,
      sortable: true,
    },
    {
      header: 'Status',
      field: 'status',
      sortable: true,
      isStatusBadge: true,
      badgeStyleGetter: (value) => {
        const map: Record<string, { background: string; color: string }> = {
          'ACTIVE':   { background: 'var(--status-available-bg, #dcfce7)', color: 'var(--status-available-text, #16a34a)' },
          'INACTIVE': { background: 'var(--status-inactive-bg, #f1f5f9)', color: 'var(--status-inactive-text, #64748b)' },
        };
        return map[value as string] ?? { background: 'transparent', color: 'inherit' };
      },
    },
    {
      field: 'actions',
      header: 'Actions',
      sortable: false,
      actions: [
        {
          icon: 'edit-icon.svg',
          buttonClass: 'primary-bg-color',
          tooltip: 'Update',
          command: (user) => this.openUpdateEmployeeDialog(user),
          visible: (row) => row['id'] != this.employee()?.id &&
            [UserRole.SUPER_ADMIN].includes(this.employee()?.role as UserRole)
        },
        {
          icon: 'trash-icon.svg',
          buttonClass: 'danger-bg-color',
          tooltip: 'Delete',
          command: (user) => this.openDeleteEmployeeDialog(user),
          visible: (row) => (row['id'] != this.employee()?.id) &&
            [UserRole.SUPER_ADMIN].includes(this.employee()?.role as UserRole)
        },
      ],
    },
  ];

  @ViewChild(EmployeeActivityLogList) activityLogList?: EmployeeActivityLogList;

  employeesList = signal<Employee[]>([]);
  totalRecords = signal(0);
  first = 0;
  rows = 20;
  activeTab = 0;

  searchControl = new FormControl('');

  selectedEmployee: Employee
  addEmployeeDialog = false
  updateEmployeeDialog = false
  deleteEmployeeDialog = false

  employee = signal<Employee | undefined>(undefined);

  roleFilter: CustomFilter = {
    key: "role",
    label: "Role",
    type: "multiselect",
    options: enumToOptions(UserRole)
  }

  statusFilter: CustomFilter = {
    key: "status",
    label: "Status",
    type: "multiselect",
    options: enumToOptions(UserStatus)
  }

  createdDateFilter: CustomFilter = {
    key: "created",
    label: "Date",
    type: "date-range",
    options: []
  }

  private employeeService = inject(EmployeeService)
  private router = inject(Router)
  private activatedRoute = inject(ActivatedRoute)


  ngOnInit() {
    this.getEmployeesList()
    this.getEmployee()
  }

  getEmployee = () => {
    this.employee = this.employeeService.employee;
  }

  getEmployeesList = () => {
    this.activatedRoute.queryParams.pipe(
      map((params) =>
        Object.fromEntries(
          Object.entries(params)
            .filter(([key]) => this.params.includes(key))
            .map(([key, value]) => [this.modifiedParams[key] || key, value])
        )
      ),

      distinctUntilChanged((previous, current) =>
        JSON.stringify(previous) === JSON.stringify(current)
      ),

      tap((queryParams) => {
        this.searchControl.setValue(queryParams['search'], { emitEvent: false });
        this.rows = queryParams['size'] ? +queryParams['size'] : 20;
      }),
      switchMap((queryParams) => {
        const params = {
          ...queryParams,
          sortBy: queryParams['sortBy'] ?? 'updatedAt',
          sortDirection: queryParams['sortDirection'] ?? 'DESC',
        };
        return this.employeeService.getEmployeesList(params);
      })
    )
      .subscribe((res) => {
        this.employeesList.set(res.data.content);
        this.totalRecords.set(res.data.totalElements)
        this.first = ((res.data.currentPage + 1) * res.data.pageSize) - res.data.pageSize
      });
  }

  onPageChange = (event: PaginatorState) => {
    const queryparams = {
      page: event.page,
      size: event.rows,
    }
    this.router.navigate([], {
      queryParams: queryparams,
      queryParamsHandling: 'merge'
    })
  }

  openAddEmployeeDialog = () => {
    this.addEmployeeDialog = true
  }

  onEmployeeAdded = () => {
    this.getEmployeesList()
    this.addEmployeeDialog = false
  }

  openUpdateEmployeeDialog = (selectedRow: Employee) => {
    this.selectedEmployee = selectedRow
    this.updateEmployeeDialog = true
  }

  onEmployeeUpdated = () => {
    this.getEmployeesList()
    this.updateEmployeeDialog = false;
  };

  openDeleteEmployeeDialog = (selectedRow: Employee) => {
    this.selectedEmployee = selectedRow
    this.deleteEmployeeDialog = true
  }

  onEmployeeDeleted = () => {
    this.getEmployeesList()
    this.deleteEmployeeDialog = false
  }

}
